import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import ConnectedAppointmentModal from '../components/ConnectedAppointmentModal';

type Props = {
  children: ReactNode;
};

export default function AppLayout({ children }: Props) {
  const { pathname } = useLocation();
  const isMessagesRoute = /^\/messages(\/|$)/.test(pathname);
  const isAppointmentsRoute = pathname === '/appointments';
  const isAppointmentPaymentRoute = /^\/appointments\/[^/]+\/payment$/.test(pathname);
  const [collapsed, setCollapsed] = useState(true);
  const isTelemedRoom = /^\/telemedicine\/[^/]+$/.test(pathname);
  const collapsedBeforeAppointments = useRef(collapsed);
  const appointmentsAutoCollapsed = useRef(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('theme') as 'light' | 'dark') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const isPatientDetails = /^\/patients\/[^/]+$/.test(pathname);
    if (isAppointmentsRoute) {
      if (!appointmentsAutoCollapsed.current) {
        collapsedBeforeAppointments.current = collapsed;
        appointmentsAutoCollapsed.current = true;
      }
      setCollapsed(true);
      return;
    }
    if (appointmentsAutoCollapsed.current) {
      appointmentsAutoCollapsed.current = false;
      setCollapsed(collapsedBeforeAppointments.current);
    }
    if (isPatientDetails || isTelemedRoom || isMessagesRoute) {
      setCollapsed(true);
      return;
    }
  }, [collapsed, isAppointmentsRoute, isMessagesRoute, isTelemedRoom, pathname]);

  return (
    <div className="min-h-screen bg-mist text-ink flex">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="flex-1 flex flex-col">
        <TopBar theme={theme} onToggleTheme={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))} />
        <main
          className={
            isTelemedRoom || isMessagesRoute || isAppointmentsRoute
              ? isAppointmentsRoute
                ? 'h-[calc(100vh-4rem)] overflow-hidden p-0'
                : 'h-[calc(100vh-4rem)] overflow-hidden'
              : isAppointmentPaymentRoute
                ? 'h-[calc(100vh-4rem)] overflow-y-auto p-0'
              : 'p-6 lg:p-10 space-y-6'
          }
        >
          {children}
        </main>
      </div>
      <ConnectedAppointmentModal />
    </div>
  );
}
